import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { ErrorAlert } from '../components/ErrorAlert';
import { Schemas } from '../lib/schemas';
import { fetchJson } from '../lib/http';
import { useNavigate, Link } from 'react-router-dom';

export function LoginPage() {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const navigate = useNavigate();

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError('');
		const parsed = Schemas.login.safeParse({ email: email.trim(), password });
		if (!parsed.success) {
			setError('Please provide a valid email and password.');
			return;
		}
		setLoading(true);
		try {
			await fetchJson('/portal/login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(parsed.data),
			});
							navigate('/custportal/dashboard', { replace: true });
		} catch (e: any) {
			if (e?.status === 429) setError('Too many attempts. Please try again in a few minutes.');
			else setError('Invalid email or password.');
		} finally {
			setLoading(false);
		}
	}

	return (
		<Layout>
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
				{/* Left: Sign in card (keeps left alignment) */}
				<div className="rounded-2xl border border-gray-200 bg-white shadow-sm elev-1 p-6 max-w-md">
					<h1 className="text-2xl font-extrabold text-gray-900">Sign in</h1>
					<form onSubmit={onSubmit} className="mt-6 space-y-4">
						<div>
							<label htmlFor="email" className="block text-sm font-semibold text-gray-700">Email</label>
							<input id="email" name="email" type="email" autoComplete="email" autoFocus required value={email} onChange={e => setEmail(e.target.value)}
								className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
						</div>
						<div>
							<label htmlFor="password" className="block text-sm font-semibold text-gray-700">Password</label>
							<input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)}
								className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
						</div>
						<ErrorAlert message={error} />
						<div className="pt-2 flex items-center gap-3">
							<button type="submit" disabled={loading} className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">
								{loading ? 'Signing in…' : 'Sign in'}
							</button>
							<Link to="/custportal/activate" className="rounded-xl border border-sedra text-sedra px-4 py-2.5 text-sm font-semibold hover:bg-sedra/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">
								Register
							</Link>
						</div>
					</form>
				</div>

				{/* Right: Welcome/info panel */}
				<div aria-label="About the customer portal" className="max-w-2xl">
					<h2 className="text-3xl font-extrabold text-gray-900">Welcome to your <span className="text-sedra">Customer Portal</span></h2>
					<p className="mt-4 text-gray-700 text-base">
						Securely view and download invoices, see payment history, and manage your account with your service provider.
					</p>
					<ul className="mt-6 space-y-3 text-gray-700">
						<li className="flex items-start gap-3"><span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sedra/10 text-sedra text-sm font-bold">1</span><span>Access invoice or payment history anytime.</span></li>
						<li className="flex items-start gap-3"><span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sedra/10 text-sedra text-sm font-bold">2</span><span>Download invoice PDFs when available.</span></li>
						<li className="flex items-start gap-3"><span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sedra/10 text-sedra text-sm font-bold">3</span><span>Need to track multiple businesses? Switch between businesses linked to your account</span></li>
					</ul>
				</div>
			</div>
		</Layout>
	);
} 